import React, { useState } from "react";
import styles from "../style.module.css";
import { Accordion } from "@/aible";
import LabledToggle from "@/components/Inputs/Toggle/Toggle";
import Tooltip from "@/components/Inputs/Tooltip";
import { toggleConversationMemory, toggleConversationMemoryIncludeReferenceData, updateConversationMemoryMessageLimit, toggleMultiSessionMemory, toggleMultiSessionMemoryCreation, updateMultiSessionMemoryCount, resetMultiSessionMemory, updateMemoryAugmentationPromptTemplate, updateMemoryCreationPromptTemplate, resetMemoryAugmentationPromptTemplate, resetMemoryCreationPromptTemplate, updateMultiSessionMemoryTypeSetting, updateMultiSessionMemoryType } from "@/containers/ChatGPT/features/ConversationMemory";
import { getConversationMemoryEnabled, getConversationMemoryMessageLimit, getConversationMemoryIncludeReferenceData, getMultiSessionMemoryEnabled, getMultiSessionMemoryCreationEnabled, getMultiSessionMemoryCount, getMemoryAugmentationPromptTemplate, getMemoryCreationPromptTemplate, getMultiSessionMemoryTypes, DEFAULT_MEMORY_CREATION_PROMPT_TEMPLATE, getMultiSessionMemoryTypeById, getRetrievalScopeOptions, DEFAULT_USER_PERSONA_CREATION_PROMPT_TEMPLATE } from "@/containers/ChatGPT/features/ConversationMemory/selectors";
import { useDispatch, useSelector } from "@/types/store";
import { LineInput } from "@/components/Inputs/LineInput";
import { Button } from "aible";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
import { toast } from "react-toastify";
import TextInput from "@/components/Inputs/TextInput";
import Select from "react-select";
function SettingsLabel(props) {
    return (React.createElement("div", { className: styles.settingFlex },
        React.createElement("div", { className: styles.settingLabel },
            props.label,
            props.tooltip && React.createElement(Tooltip, { overlay: props.tooltip })),
        props.children));
}
function ConversationMemoryToggle(props) {
    const dispatch = useDispatch();
    const conversationMemoryEnabled = useSelector(getConversationMemoryEnabled);
    return (React.createElement(SettingsLabel, { label: "Enable Conversation Memory", tooltip: "Whether or not to include previous chat history with requests to keep context between prompts." },
        React.createElement(LabledToggle, { style: { display: "block" }, checked: conversationMemoryEnabled, onChange: () => dispatch(toggleConversationMemory()), disabled: props.disableButtons, isAibleColors: true })));
}
function ConversationMemorySettingMessageLimit(props) {
    const dispatch = useDispatch();
    const conversationMemorySettingMessageLimit = useSelector(getConversationMemoryMessageLimit);
    function updateMessageLimit(val) {
        const v = Number(val);
        if (v >= 0) {
            dispatch(updateConversationMemoryMessageLimit(v));
        }
    }
    return (React.createElement(SettingsLabel, { label: "Number of previous chat messages to include", tooltip: "How many additional prompts and responses to provide more relevant responses. Additional messages will also increase the token usage." },
        React.createElement(LineInput
        // isSenseBox
        , { 
            // isSenseBox
            inputStyle: { background: "#FFFFFF" }, value: conversationMemorySettingMessageLimit, onChange: updateMessageLimit, isDisabled: props.disableButtons, type: "integer" })));
}
function ConversationMemoryIncludeReferenceData(props) {
    const dispatch = useDispatch();
    const conversationMemoryIncludeReferenceData = useSelector(getConversationMemoryIncludeReferenceData);
    return (React.createElement(SettingsLabel, { label: "Include charts sent in previous messages", tooltip: "Previous charts will increase token usage, but may provide context to additional insights." },
        React.createElement(LabledToggle, { style: { display: "block" }, checked: conversationMemoryIncludeReferenceData, onChange: () => dispatch(toggleConversationMemoryIncludeReferenceData()), disabled: props.disableButtons, isAibleColors: true })));
}
export default function ConversationMemorySettings(props) {
    const conversationMemoryEnabled = useSelector(getConversationMemoryEnabled);
    const multiSessionMemoryEnabled = useSelector(getMultiSessionMemoryEnabled);
    const multiSessionMemoryCreationEnabled = useSelector(getMultiSessionMemoryCreationEnabled);
    const headerElement = React.createElement("div", { className: styles.chatSettingsItemHeader }, "Conversation Memory");
    return (React.createElement("div", { className: styles.chatSettingsItem },
        React.createElement(Accordion, { header: headerElement },
            React.createElement(ConversationMemoryToggle, { ...props }),
            conversationMemoryEnabled && (React.createElement("div", { className: "mb-[20px]" },
                React.createElement(ConversationMemorySettingMessageLimit, { ...props }),
                React.createElement(ConversationMemoryIncludeReferenceData, { ...props }))),
            React.createElement(MultiSessionMemoryCreationToggle, { ...props }),
            multiSessionMemoryCreationEnabled && (React.createElement(MultiSessionMemoryTypeSettings, { setting: "enableCreation", allowCreation: true, ...props })),
            React.createElement(MultiSessionMemoryToggle, { ...props }),
            multiSessionMemoryEnabled && React.createElement(MultiSessionMemorySettings, { ...props }),
            React.createElement(MultiSessionMemoryReset, { ...props }))));
}
export function MultiSessionMemoryToggle(props) {
    const dispatch = useDispatch();
    const multiSessionMemoryEnabled = useSelector(getMultiSessionMemoryEnabled);
    const retLabel = props?.labelOverride ? props.labelOverride : "Use Agent Memory";
    const retDesc = props?.descriptionOverride ? props.descriptionOverride : "";
    return (React.createElement("div", { className: styles.memoryToggleSetting },
        React.createElement("div", { className: "mr-[14px]" },
            React.createElement(LabledToggle, { style: { display: "block" }, checked: multiSessionMemoryEnabled, onChange: () => dispatch(toggleMultiSessionMemory(props?.skipLocalStorageUpdate)), disabled: props.disableButtons, isAibleColors: true })),
        React.createElement("div", null,
            React.createElement("span", { className: "bold" }, retLabel),
            React.createElement("span", null, retDesc))));
}
export function MultiSessionMemoryCreationToggle(props) {
    const dispatch = useDispatch();
    const multiSessionMemoryCreationEnabled = useSelector(getMultiSessionMemoryCreationEnabled);
    const retLabel = props?.labelOverride ? props.labelOverride : "Save Agent Memories: ";
    const retDesc = props?.descriptionOverride ? props.descriptionOverride : `Save chat instructions to agent memory.`;
    return (React.createElement("div", { className: styles.memoryToggleSetting },
        React.createElement("div", { className: "mr-[14px]" },
            React.createElement(LabledToggle, { style: { display: "block" }, checked: multiSessionMemoryCreationEnabled, onChange: () => dispatch(toggleMultiSessionMemoryCreation(props?.skipLocalStorageUpdate)), disabled: props.disableButtons, isAibleColors: true })),
        React.createElement("div", null,
            React.createElement("span", { className: "bold" }, retLabel),
            React.createElement("span", null, retDesc))));
}
function MultiSessionMemorySettings(props) {
    return (React.createElement("div", null,
        React.createElement(MultiSessionMemoryTypeSettings, { setting: "enableAugmentation", ...props }),
        React.createElement(MultiSessionMemoryCountInput, { ...props }),
        React.createElement(MultiSessionMemoryAugmentationPromptTemplate, { ...props })));
}
export function MultiSessionMemoryCountInput(props) {
    const dispatch = useDispatch();
    const multiSessionMemoryCount = useSelector(getMultiSessionMemoryCount);
    function updateMemoryCount(val) {
        const v = Number(val);
        if (v >= 0) {
            dispatch(updateMultiSessionMemoryCount(v));
        }
    }
    return (React.createElement(SettingsLabel, { label: "Number of previous chat messages to include", tooltip: "How many additional prompts and responses to provide more relevant responses." },
        React.createElement(LineInput
        // isSenseBox
        , { 
            // isSenseBox
            inputStyle: { background: "#FFFFFF" }, value: multiSessionMemoryCount, onChange: updateMemoryCount, isDisabled: props.disableButtons, type: "integer" })));
}
export function MultiSessionMemoryTypeSettings(props) {
    const dispatch = useDispatch();
    const memoryTypes = useSelector(getMultiSessionMemoryTypes);
    const memoryTypeList = Object.keys(memoryTypes);
    return (React.createElement("div", { className: "mb-[8px]" },
        memoryTypeList?.map((mType) => {
            return React.createElement(MemoryTypeSettingsItem, { memoryId: mType, ...props });
        }),
        props?.allowCreation && props?.setting === "enableCreation" && React.createElement(MultiSessionMemoryTypeCreation, { ...props })));
}
export function MemoryTypeSettingsItem(props) {
    const dispatch = useDispatch();
    const { setting, memoryId, hidePrompts, skipLocalStorageUpdate } = props;
    const memoryType = useSelector((state) => getMultiSessionMemoryTypeById(state, memoryId));
    const isChecked = memoryType?.[setting];
    const isCreationAugmentation = setting === "enableCreation";
    const handleClickCheckbox = () => {
        dispatch(updateMultiSessionMemoryTypeSetting(memoryType.id, setting, !isChecked, skipLocalStorageUpdate));
    };
    const handleResetPrompt = () => {
        let resetPrompt = DEFAULT_MEMORY_CREATION_PROMPT_TEMPLATE;
        if (memoryType.id === "USER_PERSONA") {
            resetPrompt = DEFAULT_USER_PERSONA_CREATION_PROMPT_TEMPLATE;
        }
        dispatch(updateMultiSessionMemoryTypeSetting(memoryType.id, "creationPromptTemplate", resetPrompt, skipLocalStorageUpdate));
    };
    return (React.createElement("div", { className: "flex flex-column mb-[6px]" },
        React.createElement("div", { className: "flex items-center justify-between w-[100%] mb-[4px]" },
            React.createElement("div", { className: "flex items-center", onClick: handleClickCheckbox },
                React.createElement("input", { style: props?.simpleView ? { margin: "0px 12px 0px 12px" } : { margin: "0px 12px 0px 4px" }, type: "checkbox", checked: isChecked, readOnly: true }),
                React.createElement("div", { className: "font-bold mr-[8px]" }, memoryType?.label),
                React.createElement("div", null, memoryType?.description)),
            isCreationAugmentation && !hidePrompts && React.createElement(Button, { onClick: handleResetPrompt }, "Reset")),
        isCreationAugmentation && !hidePrompts && (React.createElement("div", { className: "w-[100%] mb-[16px]" },
            React.createElement(TextInput, { className: "h-[100px] w-[100%] p-[4px]", value: memoryType.creationPromptTemplate ?? DEFAULT_MEMORY_CREATION_PROMPT_TEMPLATE, onChange: (val) => dispatch(updateMultiSessionMemoryTypeSetting(memoryId, "creationPromptTemplate", val, skipLocalStorageUpdate)) })))));
}
export function MultiSessionMemoryCreationPromptTemplate(props) {
    const dispatch = useDispatch();
    const memoryCreationPromptTemplate = useSelector(getMemoryCreationPromptTemplate);
    return (React.createElement("div", { className: "mb-[10px]" },
        React.createElement("div", { className: "flex flex-row justify-between mb-[4px]" },
            React.createElement("div", null, "Memory Creation Prompt"),
            React.createElement(Button, { isSmall: true, type: "secondary", style: { marginLeft: "auto" }, onClick: () => dispatch(resetMemoryCreationPromptTemplate()) }, "Reset")),
        React.createElement(TextInput, { className: "h-[100px] w-[100%] p-[4px]", value: memoryCreationPromptTemplate, onChange: (val) => dispatch(updateMemoryCreationPromptTemplate(val)) })));
}
export function MultiSessionMemoryTypeCreation(props) {
    const dispatch = useDispatch();
    const retrievalScopeOptions = useSelector((state) => getRetrievalScopeOptions(state));
    const [isCreating, updateIsCreating] = useState(false);
    const [topic, updateTopic] = useState(undefined);
    const [retrievalScope, updateRetrievalScope] = useState(retrievalScopeOptions);
    const [label, updateLabel] = useState(undefined);
    const [description, updateDescription] = useState(undefined);
    const resetDefaultSettings = () => {
        updateTopic(undefined);
        updateRetrievalScope(retrievalScopeOptions);
        updateLabel(undefined);
        updateDescription(undefined);
    };
    const handleCancelCreation = () => {
        updateIsCreating(false);
        resetDefaultSettings();
    };
    const createStringFromArray = (stringArr) => {
        return stringArr
            .flatMap((str) => str?.split(" ") ?? [])
            .filter(Boolean)
            .join("_");
    };
    const handleCreateMemoryType = () => {
        const retrievalScopeValues = retrievalScope
            ?.map((rScope) => rScope.value)
            ?.filter((o) => o !== undefined);
        const retId = createStringFromArray([label, topic, createStringFromArray(retrievalScopeValues) ?? ""]);
        const getRetrievalScope = () => {
            const retRetrievalScope = [];
            retrievalScopeValues?.forEach((rVal) => {
                if (rVal === "usecase_and_data") {
                    if (!retRetrievalScope.includes("request_type")) {
                        retRetrievalScope.push("request_type");
                    }
                    return retRetrievalScope.push("artifact_id");
                }
                if (!retRetrievalScope.includes(rVal)) {
                    retRetrievalScope.push(rVal);
                }
            });
            return retRetrievalScope;
        };
        const newMemoryType = {
            id: retId,
            topic: topic ?? "",
            enableAugmentation: true,
            retrievalScope: getRetrievalScope(),
            enableCreation: true,
            creationPromptTemplate: DEFAULT_MEMORY_CREATION_PROMPT_TEMPLATE,
            label: label,
            description: description
        };
        dispatch(updateMultiSessionMemoryType(retId, newMemoryType));
        resetDefaultSettings();
        updateIsCreating(false);
    };
    const disableCreation = topic === undefined || retrievalScope?.length < 1 || label === undefined;
    const retRetrievalScopeOptions = retrievalScopeOptions.filter((option) => !retrievalScope.some((scope) => scope?.value === option?.value));
    const selectedRetrievalScope = retrievalScopeOptions?.filter((option) => retrievalScope?.some((selected) => selected?.value === option?.value));
    const handleUpdateRetrievalScope = (selectedOptions) => {
        if (!selectedOptions?.length) {
            return updateRetrievalScope([]);
        }
        else {
            updateRetrievalScope(selectedOptions);
        }
    };
    return (React.createElement("div", { className: "mb-[16px]" },
        !isCreating && (React.createElement("div", null,
            React.createElement("a", { style: { color: "#8556EB" }, onClick: () => updateIsCreating(true) }, "Add New Agent Memory Type"))),
        isCreating && (React.createElement("div", null,
            React.createElement("div", { className: "mb-[4px]" },
                React.createElement("div", null, "Enter a topic"),
                React.createElement(LineInput, { value: topic, onChange: (val) => updateTopic(val), isDisabled: props.disableButtons })),
            React.createElement("div", { className: "mb-[4px]" },
                React.createElement("div", null, "Enter a label"),
                React.createElement(LineInput, { value: label, onChange: (val) => updateLabel(val), isDisabled: props.disableButtons })),
            React.createElement("div", { className: "mb-[4px]" },
                React.createElement("div", null, "Enter a description"),
                React.createElement(LineInput, { value: description, onChange: (val) => updateDescription(val), isDisabled: props.disableButtons })),
            React.createElement("div", { className: "mb-[4px]" },
                React.createElement("div", null, "Select Memory Retrieval Scope"),
                React.createElement(Select, { options: retRetrievalScopeOptions, value: selectedRetrievalScope, onChange: (selectedOptions) => handleUpdateRetrievalScope(selectedOptions), isDisabled: props.disableButtons, menuPlacement: "top", isMulti: true })),
            React.createElement("div", { className: "flex justify-between mt-[8px] mb-[16px]" },
                React.createElement(Button, { onClick: handleCancelCreation }, "Cancel"),
                React.createElement(Button, { onClick: handleCreateMemoryType, disabled: props.disableButtons || disableCreation }, "Create Memory Type"))))));
}
export function MultiSessionMemoryAugmentationPromptTemplate(props) {
    const dispatch = useDispatch();
    const memoryAugmentationPromptTemplate = useSelector(getMemoryAugmentationPromptTemplate);
    return (React.createElement("div", { className: "mb-[16px]" },
        React.createElement("div", { className: "flex flex-row justify-between mb-[4px]" },
            React.createElement("div", null, "Memory Augmentation Prompt"),
            React.createElement(Button, { isSmall: true, type: "secondary", style: { marginLeft: "auto" }, onClick: () => dispatch(resetMemoryAugmentationPromptTemplate()) }, "Reset")),
        React.createElement(TextInput, { className: "h-[100px] w-[100%] p-[4px]", value: memoryAugmentationPromptTemplate, onChange: (val) => dispatch(updateMemoryAugmentationPromptTemplate(val)) })));
}
export function MultiSessionMemoryReset(props) {
    const dispatch = useDispatch();
    const [isLoading, updateIsLoading] = useState(false);
    const [showResetModal, updateShowResetModal] = useState(false);
    const handleReset = async () => {
        updateIsLoading(true);
        updateShowResetModal(false);
        try {
            await dispatch(resetMultiSessionMemory());
            toast(`User Memory has been reset!`, {
                type: toast.TYPE.SUCCESS,
                position: toast.POSITION.TOP_CENTER,
                autoClose: 7500
            });
        }
        catch {
            toast(`Error resetting user memory`, {
                type: toast.TYPE.ERROR,
                position: toast.POSITION.TOP_CENTER,
                autoClose: 7500
            });
        }
        finally {
            updateIsLoading(false);
        }
    };
    return (React.createElement("div", null,
        React.createElement(Button, { onClick: () => updateShowResetModal(true), isLoading: isLoading, disabled: props.disableButtons }, "Reset Memory"),
        React.createElement(MultiSessionMemoryResetConfirmationModal, { showModal: showResetModal, updateShowModal: updateShowResetModal, handleReset: handleReset })));
}
function MultiSessionMemoryResetConfirmationModal({ showModal, updateShowModal, handleReset }) {
    return (React.createElement(Dialog, { open: showModal, onOpenChange: () => updateShowModal(false) },
        React.createElement(DialogContent, null,
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, { className: "font-bold" }, "Confirm Memory Reset")),
            React.createElement("div", null, "Are you sure you would like to delete your multi-session conversation memory? This action cannot be undone."),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { onClick: () => updateShowModal(false) }, "Cancel"),
                React.createElement(Button, { className: "border-red-50 bg-red-50 text-white", style: { color: "white", borderColor: "red", backgroundColor: "red" }, onClick: () => {
                        handleReset();
                    } }, "RESET MEMORY")))));
}
